// src/pages/Environmental.tsx — EMP status, monitoring records, environmental incidents
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_EMPS, MOCK_ENV_MONITORING, MOCK_ENV_INCIDENTS } from '../utils/mockDataTier3'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate } from '../utils/formatters'
import { Activity, AlertTriangle, CheckCircle, X, Plus, Clock, Download, Shield, Filter } from 'lucide-react'
import { MOCK_ENV_CONDITIONS, ENV_CONDITION_STATUS_META, EnvComplianceCondition, EnvConditionStatus, MOCK_NOISE_MEASUREMENTS, NoiseMeasurement, NOISE_LAEQ_THRESHOLD } from '../utils/mockDataWHSMgmt'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'

interface EMP {
  id: string
  empNumber: string
  jobId: string
  jobNumber: string
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ACTIVE' | 'CLOSED'
  approvedAt: string | null
  approvedByName: string | null
  risks: string[]
  legislation: string[]
  lastInspectionDate: string | null
  openIncidents: number
}

const EMP_STATUS: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  DRAFT:     { label: 'Draft',     bg: 'bg-gray-100',   text: 'text-gray-600',  dot: 'bg-gray-400' },
  SUBMITTED: { label: 'Submitted', bg: 'bg-amber-100',  text: 'text-amber-700', dot: 'bg-amber-500' },
  APPROVED:  { label: 'Approved',  bg: 'bg-blue-100',   text: 'text-blue-700',  dot: 'bg-blue-500' },
  ACTIVE:    { label: 'Active',    bg: 'bg-green-100',  text: 'text-green-700', dot: 'bg-green-500' },
  CLOSED:    { label: 'Closed',    bg: 'bg-gray-200',   text: 'text-gray-500',  dot: 'bg-gray-400' },
}

const ENV_INC_TYPE: Record<string, { label: string; bg: string; text: string }> = {
  SPILL:              { label: 'Spill',              bg: 'bg-red-100',    text: 'text-red-700' },
  DUST_EXCEEDANCE:    { label: 'Dust exceedance',    bg: 'bg-amber-100',  text: 'text-amber-700' },
  NOISE_EXCEEDANCE:   { label: 'Noise exceedance',   bg: 'bg-amber-100',  text: 'text-amber-700' },
  VEGETATION_DAMAGE:  { label: 'Vegetation damage',  bg: 'bg-orange-100', text: 'text-orange-700' },
  WATERWAY_IMPACT:    { label: 'Waterway impact',    bg: 'bg-blue-100',   text: 'text-blue-700' },
  OTHER:              { label: 'Other',              bg: 'bg-gray-100',   text: 'text-gray-600' },
}

// Report spill modal
// FIX: MOCK_JOBS now imported at top-level — removed require() which breaks ES modules
function ReportSpillModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (inc: any) => void }) {
  const [form, setForm] = useState({ jobId: 'j4', type: 'SPILL', location: '', description: '', immediateActions: '', quantityLitres: '' })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const notifiable = parseFloat(form.quantityLitres) >= 200

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const job = MOCK_JOBS.find(j => j.id === form.jobId)
    onSubmit({
      id: `einc_${Date.now()}`,
      jobId: form.jobId,
      jobNumber: job?.jobNumber ?? '',
      incidentType: form.type,
      dateTime: new Date().toISOString(),
      location: form.location,
      description: form.description,
      immediateActions: form.immediateActions,
      quantitySpilledLitres: parseFloat(form.quantityLitres) || null,
      notifiableToEPA: notifiable,
      status: 'OPEN',
      closedAt: null,
      closedByName: null,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-green-50">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Activity className="w-5 h-5 text-green-600" />Report environmental event</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {notifiable && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-sm text-red-800">
              <strong>⚠ Spill ≥200L — EPA notification required within 24 hours</strong> (Environmental Protection Act 1994 s.320). Contact WHSO immediately.
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Job</label>
            <select className="input" value={form.jobId} onChange={e => set('jobId', e.target.value)}>
              {MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).map(j => (
                <option key={j.id} value={j.id}>{j.jobNumber} — {j.description}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Incident type</label>
              <select className="input" value={form.type} onChange={e => set('type', e.target.value)}>
                {Object.entries(ENV_INC_TYPE).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            {form.type === 'SPILL' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Quantity spilled (litres)</label>
                <input type="number" className={`input ${notifiable ? 'border-red-400' : ''}`} value={form.quantityLitres} onChange={e => set('quantityLitres', e.target.value)} placeholder="Approx. litres" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
            <input className="input" value={form.location} onChange={e => set('location', e.target.value)} required placeholder="Specific location on site" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">What happened?</label>
            <textarea className="input h-20 resize-none" value={form.description} onChange={e => set('description', e.target.value)} required placeholder="Description of the environmental event…" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Immediate actions taken</label>
            <textarea className="input h-16 resize-none" value={form.immediateActions} onChange={e => set('immediateActions', e.target.value)} placeholder="Containment, clean-up, notifications made…" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className={`btn-primary ${notifiable ? 'bg-red-600 hover:bg-red-700' : ''}`}>Submit report</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function Environmental() {
  const { isTier } = useAuthStore()
  const [emps, setEmps]                   = useState<EMP[]>(MOCK_EMPS as EMP[])
  const [monitoring]                      = useState(MOCK_ENV_MONITORING)
  const [envIncidents, setEnvIncidents]   = useState(MOCK_ENV_INCIDENTS)
  const [showSpillReport, setShowSpillReport] = useState(false)
  const [activeTab, setActiveTab]         = useState<'emps'|'monitoring'|'incidents'|'compliance'|'noise'>('emps')
  const [envConditions]                    = useState<EnvComplianceCondition[]>(MOCK_ENV_CONDITIONS)
  const [condFilterStatus, setCondFilterStatus] = useState<EnvConditionStatus | 'ALL'>('ALL')
  const [condJobFilter, setCondJobFilter]  = useState('ALL')

  // Live environmental records count from Neon
  const { data: liveEnvRes } = useQuery({
    queryKey: ['whs-environmental'],
    queryFn:  async () => {
      const { data } = await api.get('/api/whs/environmental')
      return data as any
    },
    staleTime: 60_000,
  })
  const liveEnvCount: number = (liveEnvRes as any)?.count ?? 0

  const awaitingApproval = emps.filter(e => e.status === 'SUBMITTED').length
  const exceedances      = monitoring.filter(m => m.exceedance).length
  const openEnvInc       = envIncidents.filter(i => i.status === 'OPEN').length
  const condOverdue      = envConditions.filter(c => c.status === 'MONITORING_OVERDUE').length
  const condExceedance   = envConditions.filter(c => c.status === 'EXCEEDANCE').length

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Environmental</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Environmental management plans and monitoring
            {liveEnvCount > 0 && <span className="ml-2 text-blue-600 font-medium">· {liveEnvCount} live from Neon</span>}
            {awaitingApproval > 0 && <span className="ml-2 text-amber-600 font-medium">· {awaitingApproval} EMP awaiting approval</span>}
            {exceedances > 0 && <span className="ml-2 text-red-600 font-medium">· {exceedances} exceedance{exceedances !== 1 ? 's' : ''} recorded</span>}
          </p>
        </div>
        <button onClick={() => setShowSpillReport(true)} className="btn-secondary flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-orange-500" />Report spill
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {([
          { key: 'emps',       label: `EMPs (${emps.length})` },
          { key: 'monitoring', label: `Monitoring${exceedances > 0 ? ` ⚠${exceedances}` : ''}` },
          { key: 'incidents',  label: `Env incidents${openEnvInc > 0 ? ` (${openEnvInc})` : ''}` },
            { key: 'compliance',  label: `Compliance matrix${condOverdue+condExceedance > 0 ? ` ⚠${condOverdue+condExceedance}` : ''}` },
          { key: 'noise',       label: `Occupational noise${MOCK_NOISE_MEASUREMENTS.filter(n=>n.actionRequired).length > 0 ? ` ⚠${MOCK_NOISE_MEASUREMENTS.filter(n=>n.actionRequired).length}` : ''}` },
      ] as const).map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${activeTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* EMPs */}
      {activeTab === 'emps' && (
        <div className="space-y-3">
          {awaitingApproval > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span><strong>{awaitingApproval} EMP submitted for approval.</strong> Work involving earthworks or site clearing cannot commence until EMP is approved.</span>
            </div>
          )}
          <div className="grid gap-4">
            {emps.map(emp => {
              const ss = EMP_STATUS[emp.status] ?? EMP_STATUS.DRAFT
              return (
                <div key={emp.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-900">{emp.empNumber}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{emp.jobNumber}</span>
                          {emp.openIncidents > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{emp.openIncidents} open incident</span>}
                        </div>
                        {emp.approvedAt && <p className="text-xs text-gray-400">Approved by {emp.approvedByName} on {formatDate(emp.approvedAt)}</p>}
                        {emp.lastInspectionDate && <p className="text-xs text-gray-400">Last inspection: {formatDate(emp.lastInspectionDate)}</p>}
                      </div>
                      <div className="flex gap-2">
                        {isTier('B_PM') && emp.status === 'SUBMITTED' && (
                          <button onClick={() => setEmps(prev => prev.map(e => e.id === emp.id ? { ...e, status: 'ACTIVE' as const, approvedAt: new Date().toISOString().slice(0,10), approvedByName: 'Jane PM' } : e))}
                            className="btn-primary text-xs flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />Approve EMP</button>
                        )}
                        <button className="btn-secondary text-xs">View EMP</button>
                      </div>
                    </div>
                    {emp.risks.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-1.5">Key environmental risks:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {emp.risks.map((r, i) => <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{r}</span>)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Monitoring */}
      {activeTab === 'monitoring' && (
        <div className="space-y-4">
          {exceedances > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span><strong>{exceedances} monitoring exceedance{exceedances !== 1 ? 's' : ''} recorded.</strong> Review and confirm corrective action taken.</span>
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Monitoring records</h3>
              {isTier('B_PM') && <button className="text-xs text-blue-600 hover:underline">+ Add record</button>}
            </div>
            <div className="divide-y divide-gray-100">
              {monitoring.length === 0
                ? <div className="py-10 text-center text-gray-400 text-sm">No monitoring records yet</div>
                : monitoring.map(m => (
                    <div key={m.id} className={`px-5 py-4 ${m.exceedance ? 'bg-red-50/50' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{m.monitoringType}</span>
                            <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{m.jobId}</span>
                            {m.exceedance && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">⚠ EXCEEDANCE</span>}
                          </div>
                          <p className="text-sm font-medium text-gray-900">{m.location}</p>
                          {m.noiseDBA != null && (
                            <p className="text-sm text-gray-700 mt-0.5">
                              Recorded: <strong className={m.exceedance ? 'text-red-600' : 'text-gray-900'}>{m.noiseDBA} dB(A)</strong>
                              {m.noiseLimit && <span className="text-gray-500 ml-1">(limit: {m.noiseLimit} dB(A))</span>}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">{new Date(m.monitoredAt).toLocaleString('en-AU',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})} · {m.monitoredByName}</p>
                          <p className="text-xs text-gray-500 mt-1 italic">{m.notes}</p>
                        </div>
                        {m.exceedance ? <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" /> : <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Environmental incidents */}
      {activeTab === 'incidents' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setShowSpillReport(true)} className="btn-secondary flex items-center gap-2 text-sm"><Plus className="w-4 h-4" />Report environmental incident</button>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
            {envIncidents.length === 0
              ? <div className="py-12 text-center text-gray-400"><Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No environmental incidents recorded</p></div>
              : envIncidents.map(inc => {
                  const it = ENV_INC_TYPE[inc.incidentType] ?? ENV_INC_TYPE.OTHER
                  return (
                    <div key={inc.id} className="px-5 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${it.bg} ${it.text}`}>{it.label}</span>
                            <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{inc.jobNumber}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${inc.status === 'CLOSED' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{inc.status}</span>
                            {inc.notifiableToEPA && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">⚠ EPA notifiable</span>}
                          </div>
                          <p className="text-sm font-medium text-gray-900">{inc.location}</p>
                          <p className="text-sm text-gray-600 mt-0.5">{inc.description}</p>
                          {inc.immediateActions && <p className="text-xs text-gray-500 mt-1"><strong>Immediate actions:</strong> {inc.immediateActions}</p>}
                          <p className="text-xs text-gray-400 mt-1">{new Date(inc.dateTime).toLocaleString('en-AU',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</p>
                          {inc.quantitySpilledLitres && <p className="text-xs text-gray-500">Quantity: <strong>{inc.quantitySpilledLitres}L</strong></p>}
                          {inc.closedAt && <p className="text-xs text-green-600">Closed {formatDate(inc.closedAt)} by {inc.closedByName}</p>}
                        </div>
                      </div>
                    </div>
                  )
                })
            }
          </div>
        </div>
      )}

      {/* ── Environmental Compliance Matrix (G-11) ── */}
      {activeTab === 'compliance' && (
        <div className="space-y-4">
          {(condOverdue > 0 || condExceedance > 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0"/>
              <span>{condOverdue > 0 && <><strong>{condOverdue} monitoring obligation{condOverdue!==1?'s':''} overdue</strong>. </>}{condExceedance > 0 && <><strong>{condExceedance} exceedance{condExceedance!==1?'s':''}</strong> recorded requiring DES/council notification.</>}</span>
            </div>
          )}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
            <div className="font-semibold mb-1">Environmental Protection Act 1994 (Qld) — conditions of approval compliance</div>
            This matrix tracks compliance with conditions of approval issued by DES, Council, TMR, and contract environmental obligations. Exceedances must be reported to the relevant authority within required timeframes.
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {(['ALL','COMPLIANT','MONITORING_OVERDUE','EXCEEDANCE','PENDING_RESPONSE'] as (EnvConditionStatus|'ALL')[]).map(f => {
              const count = f==='ALL' ? envConditions.length : envConditions.filter(c=>c.status===f).length
              if (count===0 && f!=='ALL') return null
              return <button key={f} onClick={()=>setCondFilterStatus(f)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${condFilterStatus===f?'bg-[#1F3864] text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f==='ALL'?'All':(ENV_CONDITION_STATUS_META as any)[f]?.label} <span className="opacity-60">{count}</span></button>
            })}
            <select className="input text-sm w-auto ml-auto" value={condJobFilter} onChange={e=>setCondJobFilter(e.target.value)}>
              <option value="ALL">All job sites</option>
              {MOCK_JOBS.filter(j=>!['COMPLETED','CLOSED'].includes(j.status)).map(j=><option key={j.id} value={j.id}>{j.jobNumber}</option>)}
            </select>
            <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export compliance report</button>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {['Source','Condition #','Obligation','Monitoring method','Frequency','Last checked','Next due','Status','Evidence'].map(h=><th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {envConditions
                  .filter(c=>(condFilterStatus==='ALL'||c.status===condFilterStatus)&&(condJobFilter==='ALL'||c.jobId===condJobFilter))
                  .map((cond,i) => {
                    const sm = ENV_CONDITION_STATUS_META[cond.status]
                    const SOURCE_LABEL: Record<string,string> = { DES:'DES', COUNCIL:'Council', TMR_ENV:'TMR Env', CONTRACT:'Contract' }
                    return (
                      <tr key={cond.id} className={`border-b last:border-0 ${cond.status==='EXCEEDANCE'?'bg-red-50/40':cond.status==='MONITORING_OVERDUE'?'bg-amber-50/30':''}`}>
                        <td className="px-4 py-3"><span className="text-[10px] font-medium bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{SOURCE_LABEL[cond.source]}</span><div className="text-xs text-gray-400 mt-0.5">{cond.jobNumber}</div></td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-700">{cond.conditionNumber}</td>
                        <td className="px-4 py-3 text-xs text-gray-700 max-w-xs">{cond.obligation}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{cond.monitoringMethod}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{cond.requiredFrequency}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{cond.lastChecked ?? '—'}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          {cond.nextDue
                            ? <span className={`font-medium ${new Date(cond.nextDue)<new Date()&&cond.status!=='COMPLIANT'?'text-red-600':'text-gray-700'}`}>{cond.nextDue}</span>
                            : <span className="text-gray-400">Event-triggered</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${sm.bg} ${sm.text}`}>{sm.label}</span></td>
                        <td className="px-4 py-3 text-xs text-gray-500 max-w-xs">{cond.evidence ?? <span className="text-gray-300 italic">No evidence recorded</span>}</td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Occupational Noise Monitoring (G-16) ── */}
      {activeTab === 'noise' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
            <div className="font-semibold mb-1">Occupational Noise Monitoring — WHS Reg 2011 (Qld) s.58 · Noise threshold: LAeq,8h &gt; 85 dB(A) triggers Hearing Management Program (HMP)</div>
            Workers exposed to noise above 85 dB(A) LAeq,8h must be enrolled in a Hearing Management Program including audiometric testing. Peak noise above 140 dB(C) requires immediate hearing protection upgrade.
          </div>

          {MOCK_NOISE_MEASUREMENTS.filter(n=>n.actionRequired).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/>
              <span><strong>{MOCK_NOISE_MEASUREMENTS.filter(n=>n.actionRequired).length} worker{MOCK_NOISE_MEASUREMENTS.filter(n=>n.actionRequired).length!==1?'s':''}</strong> recorded LAeq,8h above 85 dB(A). HMP enrolment required. Hearing protector upgrade action to be documented in CAR.</span>
            </div>
          )}

          {/* Exposure log table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
              <div className="font-semibold text-gray-900 text-sm">Worker noise exposure log</div>
              <button className="btn-secondary text-xs">+ Add measurement</button>
            </div>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {['Worker','Date','Work area / activity','Instrument','LAeq,8h dB(A)','Lpeak dB(C)','HMP required','Entered by'].map(h=><th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {MOCK_NOISE_MEASUREMENTS.map(nm => (
                  <tr key={nm.id} className={`border-b last:border-0 ${nm.actionRequired?'bg-red-50/30':''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{nm.workerName}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{new Date(nm.measuredAt).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'})}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{nm.activityDescription}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{nm.instrumentUsed}</td>
                    <td className="px-4 py-3">
                      <span className={`font-bold text-sm ${nm.laeq8h > NOISE_LAEQ_THRESHOLD ? 'text-red-700' : 'text-green-700'}`}>{nm.laeq8h}</span>
                      <span className="text-xs text-gray-400 ml-1">(limit {NOISE_LAEQ_THRESHOLD})</span>
                      {nm.actionRequired && <div className="text-[10px] text-red-600 font-semibold mt-0.5">⚠ EXCEEDS LIMIT</div>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{nm.lpeak}</td>
                    <td className="px-4 py-3">
                      {nm.actionRequired
                        ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">HMP required</span>
                        : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Within limits</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{nm.measuredByName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Exposure bar chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-gray-900 mb-4">LAeq,8h dB(A) — worker exposure vs 85 dB(A) threshold</div>
            <div className="space-y-3">
              {MOCK_NOISE_MEASUREMENTS.map(nm => {
                const pct = Math.min((nm.laeq8h / 110) * 100, 100)
                const thresholdPct = (NOISE_LAEQ_THRESHOLD / 110) * 100
                return (
                  <div key={nm.id}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">{nm.workerName}</span>
                      <span className={`font-bold ${nm.actionRequired?'text-red-700':'text-green-700'}`}>{nm.laeq8h} dB(A)</span>
                    </div>
                    <div className="relative h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${nm.actionRequired?'bg-red-500':'bg-green-500'}`} style={{width:`${pct}%`}}/>
                      {/* Threshold line */}
                      <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-gray-600" style={{left:`${thresholdPct}%`}} title={`${NOISE_LAEQ_THRESHOLD} dB(A) limit`}/>
                    </div>
                  </div>
                )
              })}
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                <div className="flex items-center gap-1"><div className="w-6 border-t-2 border-dashed border-gray-600"/><span>85 dB(A) limit</span></div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500"/><span>Above limit — HMP required</span></div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-500"/><span>Within limits</span></div>
              </div>
            </div>
          </div>

          {/* Hearing monitoring register section */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="font-semibold text-gray-900 text-sm mb-3">Hearing Monitoring (Audiometric) Register</div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 mb-3">
              Workers enrolled in HMP require baseline audiometric testing within 3 months of enrolment and annually thereafter. Records are subject to a 30-year retention requirement (WHS Reg 2011 s.330).
            </div>
            <div className="overflow-hidden">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="bg-gray-50"><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 border-b">Worker</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 border-b">HMP enrolled</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 border-b">Baseline test</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 border-b">Next annual test</th><th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 border-b">Status</th></tr></thead>
                <tbody>
                  {MOCK_NOISE_MEASUREMENTS.filter(n=>n.actionRequired).map(nm => (
                    <tr key={nm.id} className="border-b last:border-0">
                      <td className="px-4 py-3 font-medium text-gray-900">{nm.workerName}</td>
                      <td className="px-4 py-3 text-xs text-amber-700 font-medium">Required — not yet enrolled</td>
                      <td className="px-4 py-3 text-xs text-gray-400 italic">Pending enrolment</td>
                      <td className="px-4 py-3 text-xs text-gray-400 italic">Pending</td>
                      <td className="px-4 py-3"><span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Enrolment overdue</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end"><button className="btn-secondary text-sm">Export noise monitoring records (PDF)</button></div>
        </div>
      )}

      {showSpillReport && (
        <ReportSpillModal
          onClose={() => setShowSpillReport(false)}
          onSubmit={inc => { setEnvIncidents(prev => [inc, ...prev]); setShowSpillReport(false) }}
        />
      )}
    </div>
  )
}
